import React, { useState, useCallback, memo, useRef } from "react"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import Button from "@material-ui/core/Button"
import IconSparcaAdd from "@/assets/icons/IconSparcaAdd"
import { makeStyles } from "@material-ui/core/styles"
import ControlPanelTextField from "@/components/common/ControlPanelTextField"
import validateAddParticipantForm from "@/helpers/validateAddParticipantForm"
import useDebounce from "@/hooks/useDebounce"
import { addParticipantRequest } from "@/redux/actions/participantAction"
import ModalBulkAddParticipant from "@/components/control-panel/control-panel-modal/ModalBulkAddParticipant"
import useHandleModalShow from "@/hooks/useHandleModalShow"

const useStyles = makeStyles((theme) => ({
    formWrapper: {
        padding: theme.spacing(6, 4),
    },
    controlPanelSidebarFormIcon: {
        marginRight: theme.spacing(2),
    },
    controlPanelSidebarFormTitle: {
        flex: "0 0 auto",
        color: "#ffffff",
    },
    button: {
        whiteSpace: "nowrap",
    },
}))

const initNewParticipantManuallyAddDetail = {
    participantFirstName: "",
    participantLastName: "",
    participantEmail: "",
}

const initAddingManuallyFormStatus = {
    participantFirstName: {
        status: false,
        errorMessage: "",
    },
    participantLastName: { status: false, errorMessage: "" },
    participantEmail: { status: false, errorMessage: "" },
    overAll: false,
}

const AddNewForm = memo(() => {
    const classes = useStyles()
    const [newParticipantManuallyAddDetail, setNewParticipantManuallyAddDetail] = useState(initNewParticipantManuallyAddDetail)
    const [isAddingManuallyFormInvalid, setIsAddingManuallyFormInvalid] = useState(initAddingManuallyFormStatus)
    const dispatch = useDispatch()
    const { projectId } = useParams()
    const handleAddParticipant = useDebounce(
        async () => {
            const validateParticipantFirstName = validate({
                value: newParticipantManuallyAddDetail.participantFirstName,
                key: "participantFirstName",
            })
            const validateParticipantLastName = validate({
                value: newParticipantManuallyAddDetail.participantLastName,
                key: "participantLastName",
            })
            const validateParticipantEmail = validate({
                value: newParticipantManuallyAddDetail.participantEmail,
                key: "participantEmail",
                extraCheck: "email",
            })
            const validateStatus = validateParticipantFirstName && validateParticipantLastName && validateParticipantEmail
            if (validateStatus) {
                const projectIdInt = parseInt(projectId, 10)
                if (newParticipantManuallyAddDetail) {
                    const participantNewDetail = {
                        projectId: projectIdInt,
                        firstName: newParticipantManuallyAddDetail.participantFirstName,
                        lastName: newParticipantManuallyAddDetail.participantLastName,
                        email: newParticipantManuallyAddDetail.participantEmail,
                    }
                    await dispatch(addParticipantRequest(participantNewDetail))
                    setNewParticipantManuallyAddDetail(initNewParticipantManuallyAddDetail)
                }
            }
        },
        500,
        [isAddingManuallyFormInvalid, newParticipantManuallyAddDetail]
    )

    const validate = useCallback(({ value, key, extraCheck = null }) => {
        const validateResult = validateAddParticipantForm({ value, key, extraCheck }, setIsAddingManuallyFormInvalid)
        return validateResult
    }, [])

    const handleFormChange = useCallback(
        (event) => {
            setIsAddingManuallyFormInvalid(initAddingManuallyFormStatus)
            setNewParticipantManuallyAddDetail((prev) => ({
                ...prev,
                [event.target.name]: event.target.value,
            }))
        },
        [isAddingManuallyFormInvalid, newParticipantManuallyAddDetail]
    )

    const handleBulkAddParticipant = useCallback(() => {
        handleModalBulkAddShowChange(true)
    }, [])

    const modalBulkAddParticipantRef = useRef()
    const {
        modalItemShow: modalBulkAddShow,
        handleClose: handleBulkAddClose,
        handleModalItemShowChange: handleModalBulkAddShowChange,
    } = useHandleModalShow(modalBulkAddParticipantRef.current && modalBulkAddParticipantRef.current.resetAll)

    return (
        <>
            <Box className={classes.formWrapper}>
                <Box display="flex" alignItems="center">
                    <IconSparcaAdd width="25" fill="white" className={classes.controlPanelSidebarFormIcon} />
                    <Typography variant="h6" component="h2" className={classes.controlPanelSidebarFormTitle}>
                        New Participants
                    </Typography>
                </Box>
                <Box className="project-detail-bottom-wrapper" mt={4}>
                    <Box mb={3}>
                        <Box>
                            <Box my={2}>
                                <ControlPanelTextField
                                    required
                                    label="First Name"
                                    error={isAddingManuallyFormInvalid.participantFirstName.status}
                                    helperText={isAddingManuallyFormInvalid.participantFirstName.status && "First name required"}
                                    id="participant-first-name"
                                    name="participantFirstName"
                                    placeholder="Enter First Name"
                                    variant="outlined"
                                    onChange={handleFormChange}
                                    value={newParticipantManuallyAddDetail.participantFirstName}
                                />
                            </Box>
                            <Box my={2}>
                                <ControlPanelTextField
                                    required
                                    label="Last Name"
                                    error={isAddingManuallyFormInvalid.participantLastName.status}
                                    helperText={isAddingManuallyFormInvalid.participantLastName.status && "Last name required"}
                                    id="participant-last-name"
                                    name="participantLastName"
                                    placeholder="Enter Last Name"
                                    variant="outlined"
                                    onChange={handleFormChange}
                                    value={newParticipantManuallyAddDetail.participantLastName}
                                />
                            </Box>
                            <Box my={2}>
                                <ControlPanelTextField
                                    required
                                    label="Email"
                                    error={isAddingManuallyFormInvalid.participantEmail.status}
                                    helperText={
                                        isAddingManuallyFormInvalid.participantEmail.status &&
                                        (isAddingManuallyFormInvalid.participantEmail.errorMessage
                                            ? isAddingManuallyFormInvalid.participantEmail.errorMessage
                                            : "Email required")
                                    }
                                    id="participant-email"
                                    name="participantEmail"
                                    placeholder="Enter Email"
                                    variant="outlined"
                                    onChange={handleFormChange}
                                    value={newParticipantManuallyAddDetail.participantEmail}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Box>
                        <Box my={2}>
                            <Button
                                className={classes.button}
                                variant="contained"
                                color="primary"
                                aria-label="add"
                                onClick={handleAddParticipant}
                            >
                                Add new participant
                            </Button>
                        </Box>
                        <Box my={2}>
                            <Button
                                className={classes.button}
                                variant="contained"
                                color="primary"
                                aria-label="add"
                                onClick={handleBulkAddParticipant}
                            >
                                Bulk add participants
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <ModalBulkAddParticipant
                ref={modalBulkAddParticipantRef}
                modalBulkAddShow={modalBulkAddShow}
                handleClose={handleBulkAddClose}
            />
        </>
    )
})

export default AddNewForm
